import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { parseTextWithImages } from '../components/parseTextWithImages';

const QuestionCard: React.FC<{ question: any; userAnswer: any; questionIndex: number }> = ({ question, userAnswer, questionIndex }) => {
    const options = question.Options || question.options || [
        question.option1, question.option2, question.option3, question.option4
    ].filter(Boolean);
    
    let correctAnswer = question.Answer || question.answer || question.correct;
    if (correctAnswer && correctAnswer.startsWith('option')) {
        const optionNum = parseInt(correctAnswer.replace('option', '')) - 1;
        correctAnswer = options[optionNum];
    }
    
    const questionText = question.Text || question.text || question.Question || question.question;
    const solution = question.Solution || question.solution || question.explanation;
    const isCorrect = userAnswer === correctAnswer;
    const isSkipped = userAnswer === undefined || userAnswer === null;

    return (
        <div style={{ backgroundColor: '#ffffff', padding: '24px', marginBottom: '32px', border: '1px solid #d1d5db', borderRadius: '8px', pageBreakInside: 'avoid' }} className="break-inside-avoid">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #e5e7eb' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>Question {questionIndex + 1}</h2>
                <span className={`px-3 py-1 rounded-lg text-sm font-bold ${
                    isCorrect ? 'bg-green-100 dark:bg-green-900 dark:text-green-200 text-green-800' : 
                    isSkipped ? 'bg-gray-100 dark:bg-gray-700 dark:text-gray-200 text-gray-600' : 'bg-red-100 dark:bg-red-900 dark:text-red-200 text-red-800'
                }`}>
                    {isCorrect ? '+4' : isSkipped ? '0' : '-1'} marks
                </span>
            </div>

            <div style={{ marginBottom: '24px', color: '#1f2937', fontSize: '16px', lineHeight: '1.6' }}>
                {parseTextWithImages(questionText)}
            </div>

            <div className="mb-6 space-y-3">
                {options.map((option: string, optIndex: number) => {
                    const isCorrectOption = option === correctAnswer;
                    const isUserChoice = option === userAnswer;
                    
                    return (
                        <div key={optIndex} style={{
                            padding: '16px',
                            borderRadius: '8px',
                            border: '2px solid',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderColor: isCorrectOption ? '#10b981' : isUserChoice && !isCorrectOption ? '#ef4444' : '#d1d5db',
                            backgroundColor: isCorrectOption ? '#ecfdf5' : isUserChoice && !isCorrectOption ? '#fef2f2' : '#f9fafb'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <span style={{ fontWeight: 'bold', marginRight: '12px', fontSize: '18px', color: '#1f2937' }}>
                                    {String.fromCharCode(65 + optIndex)}.
                                </span>
                                <div style={{ flex: 1 }}>{parseTextWithImages(option)}</div>
                            </div>
                            <div style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isCorrectOption && <span style={{ color: '#059669', fontWeight: 'bold', fontSize: '14px' }}>✓ Correct</span>}
                                {isUserChoice && <span style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '14px' }}>Your Answer</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div>
                        <span style={{ fontWeight: 'bold', color: '#374151' }}>Your Answer: </span>
                        <span style={{
                            fontWeight: 'bold',
                            color: isCorrect ? '#059669' : isSkipped ? '#6b7280' : '#dc2626'
                        }}>
                            {isSkipped ? 'Not Answered' : parseTextWithImages(userAnswer)}
                        </span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold', color: '#374151' }}>Correct Answer: </span>
                        <span style={{ fontWeight: 'bold', color: '#059669' }}>{parseTextWithImages(correctAnswer)}</span>
                    </div>
                </div>
                
                {solution && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                        <div style={{ fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>Explanation:</div>
                        <div style={{ color: '#4b5563', lineHeight: '1.6' }}>{parseTextWithImages(solution)}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const PrintableResultsPage = () => {
    const { testId } = useParams<{ testId: string }>();
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);

    useEffect(() => {
        const loadData = async () => {
            const storedResults = localStorage.getItem(`testResults_${testId}`);
            const storedTest = localStorage.getItem(`testData_${testId}`);
            
            if (storedResults && storedTest) {
                setData({
                    results: JSON.parse(storedResults),
                    test: JSON.parse(storedTest)
                });
                
                // Simulate loading progress for LaTeX rendering
                for (let i = 0; i <= 100; i += 10) {
                    setLoadingProgress(i);
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
                
                // Extra time for LaTeX to fully render
                await new Promise(resolve => setTimeout(resolve, 2000));
                setIsLoading(false);
            }
        };
        
        loadData();
    }, [testId]);

    const handlePrint = () => {
        window.print();
    };

    if (!data) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4">Loading Test Data...</h1>
                    <p className="text-gray-600">Please wait while we prepare your test results.</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
                    <h1 className="text-2xl font-bold mb-4">Preparing Questions...</h1>
                    <p className="text-gray-600 mb-6">Loading questions with proper formatting</p>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden mb-4">
                        <div 
                            className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                            style={{ width: `${loadingProgress}%` }}
                        ></div>
                    </div>
                    <p className="text-sm text-gray-500">{loadingProgress}% Complete</p>
                </div>
            </div>
        );
    }

    const questions = data.test.Questions || data.test.questions || [];
    const answers = data.results.answers || {};

    return (
        <>
            {/* Print Styles */}
            <style jsx>{`
                @media print {
                    body { margin: 0; }
                    .no-print { display: none !important; }
                    .print-container { 
                        max-width: none !important; 
                        margin: 0 !important; 
                        padding: 20px !important;
                    }
                }
            `}</style>

            {/* Print Button - Hidden during print */}
            <div className="no-print fixed top-4 right-4 z-50">
                <button
                    onClick={handlePrint}
                    className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
                >
                    Print as PDF
                </button>
            </div>

            {/* Printable Content */}
            <div className="print-container max-w-4xl mx-auto p-6 min-h-screen" style={{ backgroundColor: '#ffffff', color: '#1f2937' }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '32px', paddingBottom: '24px', borderBottom: '2px solid #d1d5db' }}>
                    <h1 style={{ fontSize: '30px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>{data.test.name || data.test.Name}</h1>
                    <p style={{ fontSize: '18px', color: '#4b5563' }}>Test Results - Detailed Analysis</p>
                    <div className="mt-4 flex justify-center space-x-8 text-sm">
                        <span><strong>Score:</strong> {data.results.score}</span>
                        <span><strong>Accuracy:</strong> {data.results.accuracy}%</span>
                        <span><strong>Questions:</strong> {questions.length}</span>
                    </div>
                </div>

                {/* Questions */}
                {questions.map((question: any, index: number) => {
                    const userAnswer = answers[index] || answers[question.id];
                    return (
                        <QuestionCard
                            key={question.id || index}
                            question={question}
                            userAnswer={userAnswer}
                            questionIndex={index}
                        />
                    );
                })}
            </div>
        </>
    );
};